import { Vec2Mm } from '~/domain/geometry/patientGeometry';
import { convexHull } from '~/engine/util/algorithms/sdf/polygonWinding';

/** A closed ring without a repeated closing vertex. */
export type PlanarRingMm = readonly Vec2Mm[];

/** One connected part of a slice: its outer ring first, then the holes directly inside it. */
export type SlicePart = readonly PlanarRingMm[];

/** A segment between two points of a slice. */
export interface InsideChord {
    readonly a: Vec2Mm;
    readonly b: Vec2Mm;
    readonly lengthMm: number;
}

/** Rings as flat coordinates, with room for the crossings of one line. */
export interface PackedRings {
    /** x0, y0, x1, y1, … ring after ring, in mm. */
    readonly xy: Float64Array;
    /** Per ring, the index one past its last vertex. */
    readonly ringEnds: Int32Array;
    /** Crossings of the line displaced towards its positive, then its negative side. */
    readonly sides: readonly [Float64Array, Float64Array];
}

/** Thrown when an outline needs more line clipping than a search allows. */
export class OutlineTooComplexError extends Error {
    name = 'OutlineTooComplexError';
    constructor() {
        super('The outline is too complex to search for its longest chord.');
    }
}

/** Outline vertices the searches of one measurement may still visit. */
export interface ClipBudget {
    vertexVisits: number;
}

/** Takes `visits` from `budget`, throwing {@link OutlineTooComplexError} once it is spent. */
export function spend(budget: ClipBudget, visits: number): void {
    budget.vertexVisits -= visits;
    if (budget.vertexVisits < 0) {
        throw new OutlineTooComplexError();
    }
}

/** The two points of `hull` farthest apart, or null for fewer than two distinct points. */
export function feretDiameter(hull: readonly Vec2Mm[]): InsideChord | null {
    let [bestSq, bestI, bestJ] = [0, 0, 0];
    for (let i = 0; i < hull.length; i++) {
        for (let j = i + 1; j < hull.length; j++) {
            const dx = hull[j][0] - hull[i][0];
            const dy = hull[j][1] - hull[i][1];
            if (dx * dx + dy * dy > bestSq) {
                [bestSq, bestI, bestJ] = [dx * dx + dy * dy, i, j];
            }
        }
    }
    return bestSq > 0
        ? { a: hull[bestI], b: hull[bestJ], lengthMm: Math.sqrt(bestSq) }
        : null;
}

export function packRings(rings: readonly PlanarRingMm[]): PackedRings {
    const xy = Float64Array.from(rings.flat(2));
    let end = 0;
    return {
        xy,
        ringEnds: Int32Array.from(rings, (ring) => (end += ring.length)),
        // An edge crosses a line at most once.
        sides: [
            new Float64Array(xy.length / 2),
            new Float64Array(xy.length / 2),
        ],
    };
}

/** Sorts the first `count` values in place: a line crosses an outline a handful of times. */
function sortPrefix(values: Float64Array, count: number): void {
    for (let i = 1; i < count; i++) {
        const value = values[i];
        let j = i - 1;
        for (; j >= 0 && values[j] > value; j--) {
            values[j + 1] = values[j];
        }
        values[j + 1] = value;
    }
}

/**
 * The longest stretch of the line through `(ox, oy)` along `(dx, dy)` inside
 * the rings (even-odd, boundary included), written to `span` as [start, end] in
 * mm; false when there is none. With `through`, only a stretch holding that
 * point of the line counts. The line is clipped displaced towards each side and
 * the two clips are united, so the result does not depend on how a ring is
 * wound.
 */
export function longestInsideSpan(
    rings: PackedRings,
    ox: number,
    oy: number,
    dx: number,
    dy: number,
    mergeGapMm: number,
    span: Float64Array,
    through?: number
): boolean {
    const length = Math.hypot(dx, dy);
    if (!(length > 0) || !Number.isFinite(length)) {
        throw new Error('A line needs a direction of finite, non-zero length.');
    }
    const { xy, ringEnds } = rings;
    const [positive, negative] = rings.sides;
    let [positiveCount, negativeCount, ringStart] = [0, 0, 0];
    for (const ringEnd of ringEnds) {
        let x0 = xy[2 * ringEnd - 2];
        let y0 = xy[2 * ringEnd - 1];
        // The unnormalised cross product: exactly 0 at a vertex the line was
        // built through.
        let f0 = dx * (y0 - oy) - dy * (x0 - ox);
        for (let i = ringStart; i < ringEnd; i++) {
            const x1 = xy[2 * i];
            const y1 = xy[2 * i + 1];
            const f1 = dx * (y1 - oy) - dy * (x1 - ox);
            const crossesPositive = f0 > 0 !== f1 > 0;
            const crossesNegative = f0 < 0 !== f1 < 0;
            if (crossesPositive || crossesNegative) {
                // A vertex on the line is the crossing itself, so both its
                // edges report the same parameter.
                const s = f0 / (f0 - f1);
                const px = f1 === 0 ? x1 : x0 + s * (x1 - x0);
                const py = f1 === 0 ? y1 : y0 + s * (y1 - y0);
                const t = ((px - ox) * dx + (py - oy) * dy) / length;
                if (crossesPositive) {
                    positive[positiveCount++] = t;
                }
                if (crossesNegative) {
                    negative[negativeCount++] = t;
                }
            }
            x0 = x1;
            y0 = y1;
            f0 = f1;
        }
        ringStart = ringEnd;
    }
    sortPrefix(positive, positiveCount);
    sortPrefix(negative, negativeCount);

    let [i, j] = [0, 0];
    let [start, end] = [NaN, NaN];
    let bestLength = -1;
    const keepCurrent = () => {
        const holds =
            through === undefined ||
            (start - mergeGapMm <= through && through <= end + mergeGapMm);
        if (holds && end - start > bestLength) {
            bestLength = end - start;
            span[0] = start;
            span[1] = end;
        }
    };
    // Each side's crossings pair into sorted intervals, united here in order
    // of their starts.
    while (i < positiveCount || j < negativeCount) {
        const fromPositive =
            j >= negativeCount ||
            (i < positiveCount && positive[i] <= negative[j]);
        const side = fromPositive ? positive : negative;
        const at = fromPositive ? i : j;
        i += fromPositive ? 2 : 0;
        j += fromPositive ? 0 : 2;
        if (side[at] - end <= mergeGapMm) {
            end = Math.max(end, side[at + 1]);
        } else {
            keepCurrent();
            start = side[at];
            end = side[at + 1];
        }
    }
    keepCurrent();
    return bestLength >= 0;
}

/** Directions over [0, π) at which the hull's width is tabulated. */
const STEPS = 3600;
const STEP_RAD = Math.PI / STEPS;

/**
 * The longest segment inside `part` (holes excluded), or null when none is at
 * least `floorMm` long: the outer ring's Feret diameter when it lies inside,
 * else the best of the lines through every vertex pair. Throws {@link
 * OutlineTooComplexError} once `budget` is spent.
 */
export function longestInsideChord(
    part: SlicePart,
    floorMm: number,
    mergeGapMm: number,
    budget: ClipBudget
): InsideChord | null {
    const corners = convexHull(part[0]);
    const feret = feretDiameter(corners);
    // Bound: the part lies in its outer ring's hull.
    if (!feret || feret.lengthMm < floorMm) {
        return null;
    }
    const rings = packRings(part);
    const hull = packRings([corners]);
    const span = new Float64Array(2);
    let best: InsideChord | null = null;
    let bestMm = 0;
    const clip = (ox: number, oy: number, dx: number, dy: number) => {
        spend(budget, rings.xy.length / 2);
        if (
            longestInsideSpan(rings, ox, oy, dx, dy, mergeGapMm, span) &&
            span[1] - span[0] > bestMm
        ) {
            const n = Math.hypot(dx, dy);
            bestMm = span[1] - span[0];
            best = {
                a: [ox + (span[0] * dx) / n, oy + (span[0] * dy) / n],
                b: [ox + (span[1] * dx) / n, oy + (span[1] * dy) / n],
                lengthMm: bestMm,
            };
        }
    };

    const [[ax, ay], [bx, by]] = [feret.a, feret.b];
    clip(ax, ay, bx - ax, by - ay);
    // No stretch of the Feret line is longer than the Feret segment itself.
    if (bestMm >= feret.lengthMm - mergeGapMm) {
        return feret;
    }

    // The hull's extent along each direction `k·STEP_RAD`.
    const widths = Float64Array.from({ length: STEPS }, (_, k) => {
        const [ex, ey] = [Math.cos(k * STEP_RAD), Math.sin(k * STEP_RAD)];
        let [min, max] = [Infinity, -Infinity];
        for (let i = 0; i < hull.xy.length; i += 2) {
            const projection = hull.xy[i] * ex + hull.xy[i + 1] * ey;
            min = Math.min(min, projection);
            max = Math.max(max, projection);
        }
        return max - min;
    });
    // A hull's width changes by at most its Feret diameter per radian.
    const slackMm = feret.lengthMm * STEP_RAD;
    const cannotWin = (boundMm: number) =>
        boundMm <= bestMm || boundMm < floorMm;
    const { xy } = rings;
    for (let i = 0; i < xy.length; i += 2) {
        const [ox, oy] = [xy[i], xy[i + 1]];
        for (let j = i + 2; j < xy.length; j += 2) {
            const dx = xy[j] - ox;
            const dy = xy[j + 1] - oy;
            const angle = Math.atan2(dy, dx);
            const step =
                Math.floor((angle < 0 ? angle + Math.PI : angle) / STEP_RAD) %
                STEPS;
            // Bounds: a segment on this line lies in the hull, so it is no
            // longer than the hull's width along it nor the line's chord of it.
            if (
                (dx !== 0 || dy !== 0) &&
                !cannotWin(widths[step] + slackMm) &&
                longestInsideSpan(hull, ox, oy, dx, dy, mergeGapMm, span) &&
                !cannotWin(span[1] - span[0])
            ) {
                clip(ox, oy, dx, dy);
            }
        }
    }
    return bestMm >= floorMm ? best : null;
}

/**
 * The longest unbroken segment inside `part` perpendicular to `long` and
 * crossing it between its ends, or null. Its length is linear between vertices
 * as the line sweeps along `long`, so only the lines through vertices and the
 * ends of `long` are tried. Throws {@link OutlineTooComplexError} once `budget`
 * is spent.
 */
export function longestPerpendicularChord(
    part: SlicePart,
    long: InsideChord,
    mergeGapMm: number,
    budget: ClipBudget
): InsideChord | null {
    const [[ax, ay], [bx, by]] = [long.a, long.b];
    const longMm = Math.hypot(bx - ax, by - ay);
    const [ux, uy] = [(bx - ax) / longMm, (by - ay) / longMm];
    // Same unit direction, so the two segments are exactly perpendicular.
    const [nx, ny] = [-uy, ux];

    // Each line to try as [position along `long`, x, y].
    const stops = [
        [0, ax, ay],
        [longMm, bx, by],
        ...part.flat().map(([x, y]) => [(x - ax) * ux + (y - ay) * uy, x, y]),
    ]
        .filter(([position]) => position >= 0 && position <= longMm)
        .sort((p, q) => p[0] - q[0]);

    const rings = packRings(part);
    const span = new Float64Array(2);
    let best: InsideChord | null = null;
    for (const [, x, y] of stops) {
        spend(budget, rings.xy.length / 2);
        // Where the line meets `long`, in mm along it from (x, y).
        const through = (ax - x) * nx + (ay - y) * ny;
        if (
            longestInsideSpan(rings, x, y, nx, ny, mergeGapMm, span, through) &&
            span[1] - span[0] > (best?.lengthMm ?? 0)
        ) {
            best = {
                a: [x + span[0] * nx, y + span[0] * ny],
                b: [x + span[1] * nx, y + span[1] * ny],
                lengthMm: span[1] - span[0],
            };
        }
    }
    return best;
}
